import React, { useEffect, useState } from 'react';
import { useLiceos } from '../../../application/liceos/hooks/useLiceos';
import ToolbarComponent from '../../ui/section';
import Loader from '../../ui/loader';
import './stats.css';

const LiceoStatsPage = (props) => {
    const { 
        getLiceoById,
        getLiceoStats,
        getStatisticsOverview,
        isLoading, 
        error 
    } = useLiceos();
    
    const [liceo, setLiceo] = useState(null);
    const [stats, setStats] = useState(null);
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const id = props.match?.params?.id;
        if (id) {
            loadData(id);
        }
    }, [props.match?.params?.id]);

    const loadData = async (id) => {
        setLoading(true);
        try {
            const [liceoData, statsData, overviewData] = await Promise.all([
                getLiceoById(id),
                getLiceoStats(id),
                getStatisticsOverview({})
            ]);
            setLiceo(liceoData);
            setStats(statsData);
            setOverview(overviewData);
        } catch (err) {
            console.error('Error al cargar datos:', err);
        } finally {
            setLoading(false);
        }
    };

    if (loading || isLoading) {
        return (
            <ToolbarComponent label="Estadísticas del Liceo" showBackButton={true} onBackClick={() => props.history.goBack()}>
                <Loader text="Cargando estadísticas..." />
            </ToolbarComponent>
        );
    }

    if (error || !liceo) {
        return (
            <ToolbarComponent label="Estadísticas del Liceo" showBackButton={true} onBackClick={() => props.history.goBack()}>
                <div className="error-message">
                    {error || 'No se pudo cargar la información'}
                </div>
            </ToolbarComponent>
        );
    }

    return (
        <ToolbarComponent label={`Estadísticas - ${liceo.name || 'Liceo'}`} showBackButton={true} onBackClick={() => props.history.goBack()}>
            <div className="stats-container">
                {/* Información del Liceo */}
                <div className="stats-header">
                    <h1 className="stats-title">{liceo.name || 'Sin nombre'}</h1>
                    <p className="stats-subtitle">{liceo.address || '-'}</p>
                </div>

                {/* KPIs */}
                {stats && (
                    <div className="stats-section">
                        <h2 className="section-title">Indicadores Principales</h2>
                        <div className="kpi-grid">
                            {stats.totalUsers !== undefined && (
                                <div className="kpi-card">
                                    <div className="kpi-label">Total Usuarios</div>
                                    <div className="kpi-value">{stats.totalUsers || 0}</div>
                                </div>
                            )}
                            {stats.totalApplications !== undefined && (
                                <div className="kpi-card">
                                    <div className="kpi-label">Postulaciones</div>
                                    <div className="kpi-value">{stats.totalApplications || 0}</div>
                                </div>
                            )}
                            {stats.activeApplications !== undefined && (
                                <div className="kpi-card">
                                    <div className="kpi-label">Postulaciones Activas</div>
                                    <div className="kpi-value">{stats.activeApplications || 0}</div>
                                </div>
                            )}
                            {stats.totalPoints !== undefined && (
                                <div className="kpi-card">
                                    <div className="kpi-label">Puntos Totales</div>
                                    <div className="kpi-value">{stats.totalPoints || 0}</div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Estadísticas de Postulaciones */}
                {stats && stats.applications && (
                    <div className="stats-section">
                        <h2 className="section-title">Postulaciones</h2>
                        <div className="detail-grid">
                            {stats.applications.byStatus && Object.entries(stats.applications.byStatus).length > 0 && (
                                <div className="detail-card">
                                    <div className="detail-card-title">Por Estado</div>
                                    <div className="simple-list">
                                        {Object.entries(stats.applications.byStatus).map(([status, count]) => (
                                            <div key={status} className="simple-list-item">
                                                <span className="simple-list-item-label">{status}</span>
                                                <span className="simple-list-item-value">{count}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {stats.applications.byStage && Object.entries(stats.applications.byStage).length > 0 && (
                                <div className="detail-card">
                                    <div className="detail-card-title">Por Etapa</div>
                                    <div className="simple-list">
                                        {Object.entries(stats.applications.byStage).map(([stage, count]) => (
                                            <div key={stage} className="simple-list-item">
                                                <span className="simple-list-item-label">{stage}</span>
                                                <span className="simple-list-item-value">{count}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Estadísticas Generales */}
                {overview && (
                    <div className="stats-section">
                        <h2 className="section-title">Resumen General</h2>
                        <div className="detail-grid">
                            {overview.liceos && (
                                <div className="detail-card">
                                    <div className="detail-card-title">Liceos</div>
                                    <div className="simple-list">
                                        <div className="simple-list-item">
                                            <span className="simple-list-item-label">Total</span>
                                            <span className="simple-list-item-value">{overview.liceos.total || 0}</span>
                                        </div>
                                        <div className="simple-list-item">
                                            <span className="simple-list-item-label">Activos</span>
                                            <span className="simple-list-item-value">{overview.liceos.active || 0}</span>
                                        </div>
                                        <div className="simple-list-item">
                                            <span className="simple-list-item-label">Total Usuarios</span>
                                            <span className="simple-list-item-value">{overview.liceos.totalUsers || 0}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {overview.applications && (
                                <div className="detail-card">
                                    <div className="detail-card-title">Postulaciones</div>
                                    <div className="simple-list">
                                        <div className="simple-list-item">
                                            <span className="simple-list-item-label">Total</span>
                                            <span className="simple-list-item-value">{overview.applications.total || 0}</span>
                                        </div>
                                        <div className="simple-list-item">
                                            <span className="simple-list-item-label">Activas</span>
                                            <span className="simple-list-item-value">{overview.applications.active || 0}</span>
                                        </div>
                                        <div className="simple-list-item">
                                            <span className="simple-list-item-label">Pendientes</span>
                                            <span className="simple-list-item-value">{overview.applications.pending || 0}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {!stats && !overview && (
                    <div className="no-data">
                        <p>No hay estadísticas disponibles para este liceo.</p>
                    </div>
                )}
            </div>
        </ToolbarComponent>
    );
};

export default LiceoStatsPage;









